import React from "react";
import {IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import {useLocation, useNavigate} from "react-router-dom";
import {hasPrivilege} from "../../util/SessionContext";
import {Blue3, Grey2, Grey5, Grey6, Grey7} from "../../theme/styles";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import css_buttons from "../Common/css/CCButton.module.scss";
import {NAV, NavGroup, NavItem, ADMINISTRATOR_ONLY_ROUTES} from "./navModel";
import {dashboardRailItems} from "./dashboardRail";
import {DashboardSummary, getDashboards} from "../Reporting/dashboardApi";
import EventBus from "../../util/EventBus";

const CLOSED_WIDTH = 64;
const OPEN_WIDTH = 260;
const ACTIVE_BG = "#E8F4FA";

/**
 * TWO RAILS IN ONE, and they are not the same design.
 *
 * COLLAPSED it is an icon rail, 64px, and stays exactly what it was: one 40x40 button per group,
 * centred, children hidden because there is no room to render them.
 *
 * EXPANDED it follows Ataccama ONE Agentic's navigation, which is a compact TEXT list: a small icon
 * beside a group's label, its children beneath as text alone, indented to align under that label.
 * The first attempt kept the 40x40 buttons in both states, which made the expanded rail a column of
 * tall heavy tiles -- the right structure rendered in the wrong idiom, and not what it was following.
 *
 * Row heights are deliberately small (~32px group, ~26px child). A rail that names forty
 * destinations only works if a reader can see two dozen of them at once.
 */
export default function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [searchOpen, setSearchOpen] = React.useState<boolean>(false);
    const [dashboards, setDashboards] = React.useState<DashboardSummary[]>([]);

    // THE DASHBOARDS UNDER HOME. Asked for once, and again whenever one is saved or deleted --
    // otherwise the rail goes on naming a dashboard that has gone, or fails to name the one
    // somebody just made, which reads as the save not having worked.
    //
    // ONLY FOR SOMEBODY WHO MAY USE REPORTING. The privilege is all-or-nothing and most of an
    // account does not hold it; asking on their behalf returns a refusal, and a rail that logged a
    // failure on every page load would be the first thing in the console for most of the account.
    //
    // A FAILURE IS AN EMPTY RAIL, never an error: Home is still a destination, every other entry
    // still works, and the reader is told nothing they cannot act on.
    const mayReport = hasPrivilege("reporting");
    const loadDashboards = React.useCallback(() => {
        if (!mayReport) { setDashboards([]); return; }
        getDashboards().then(setDashboards).catch(() => setDashboards([]));
    }, [mayReport]);

    React.useEffect(() => { loadDashboards(); }, [loadDashboards]);

    React.useEffect(() => {
        const listener = () => loadDashboards();
        EventBus.dashboards.addListener("change-dashboards", listener);
        return () => { EventBus.dashboards.removeListener("change-dashboards", listener); };
    }, [loadDashboards]);

    const isActive = (route: string) =>
        route === "/" ? location.pathname === "/" : location.pathname === route || location.pathname.startsWith(route + "/");

    // A group reads as active when it IS the destination, or when one of its children is. Collapsed,
    // that is the only way to show where you are at all -- the child that is open is not rendered.
    // A group reads as active when it IS the destination, or when one of its children is.
    //
    // EXCEPT HOME, whose children are not its own destinations -- they are the Dashboards section's,
    // listed a second time where a reader can reach them. Letting them light Home up put TWO
    // entries in the tinted state at once, Home and Dashboards, which says the reader is in two
    // places. Home is lit when you are on Home.
    const groupActive = (group: NavGroup) =>
        (!!group.route && isActive(group.route))
        || (group.kind !== "home"
            && visibleChildren(group).some(child => !child.heading && isActive(child.route)));

    const handleClick = (group: NavGroup) => {
        if (group.kind === "search") { setSearchOpen(true); return; }
        if (group.route) { navigate(group.route); return; }
        // A group header is a heading, not a destination -- but collapsed there are no children to
        // click, so it opens the first one rather than doing nothing at all.
        const first = group.children?.[0];
        if (first) navigate(first.route);
    };

    const visibleChildren = (group: NavGroup): NavItem[] =>
        group.kind === "home"
            ? dashboardRailItems(dashboards)
            : (group.children ?? []).filter(child => !ADMINISTRATOR_ONLY_ROUTES.has(child.route)
                                                     || hasPrivilege("administrator"));

    return (
        <>
        <div style={{
            width: expanded ? OPEN_WIDTH : CLOSED_WIDTH,
            minWidth: expanded ? OPEN_WIDTH : CLOSED_WIDTH,
            transition: "width 200ms ease, min-width 200ms ease",
            backgroundColor: "#FFFFFF",
            borderRight: `1px solid ${Grey2}`,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",
        }}>
            {/* Collapse / expand toggle */}
            <div style={{
                display: "flex",
                justifyContent: expanded ? "flex-end" : "center",
                padding: "8px",
                borderBottom: `1px solid ${Grey2}`,
            }}>
                <Tooltip title={expanded ? "Collapse" : "Expand"} placement="right">
                    <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label="toggle-sidebar">
                        {expanded ? <ChevronLeft sx={{color: Grey5}}/> : <ChevronRight sx={{color: Grey5}}/>}
                    </IconButton>
                </Tooltip>
            </div>

            <div style={{display: "flex", flexDirection: "column", paddingTop: "6px", paddingBottom: "16px"}}>
                {NAV.map((group, index) => {
                    const startsWorkspace = !!group.workspace && !NAV[index - 1]?.workspace;
                    // THE RAIL'S ONE DIVIDER, above the trailing group.
                    const startsTrailing = !!group.trailing && !NAV[index - 1]?.trailing;
                    return (
                        <React.Fragment key={group.label}>
                            {startsWorkspace ? (
                                expanded ? (
                                    <Typography style={{
                                        fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                                        color: Grey6, padding: "16px 16px 6px",
                                    }}>
                                        Workspace
                                    </Typography>
                                ) : null
                            ) : null}

                            {/* THE ONLY DIVIDER IN THE RAIL. The workspace boundary above is a
                                HEADING, shown as text when there is room for it and as nothing
                                when there is not -- it used to draw a line when collapsed, which
                                put two separators in a rail that needs one. A line here marks the
                                entries that are about the app rather than destinations within it,
                                and it is the same line in every app in the suite. */}
                            {startsTrailing ? (
                                <div style={{height: "1px", backgroundColor: Grey2, margin: "8px 12px"}}/>
                            ) : null}

                            <GroupRow
                                group={group}
                                expanded={expanded}
                                active={groupActive(group)}
                                onClick={() => handleClick(group)}
                                items={visibleChildren(group)}
                                onChild={child => navigate(child.route)}
                                onSelf={() => group.route && navigate(group.route)}
                            />

                            {expanded
                                ? visibleChildren(group).map(child => (
                                    <ChildRow
                                        key={child.label + child.route}
                                        item={child}
                                        active={!child.heading && isActive(child.route)}
                                        onClick={() => { if (!child.heading) navigate(child.route); }}
                                    />
                                ))
                                : null}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)}/>
        </>
    );
}

function GroupRow({group, expanded, active, onClick, items, onChild, onSelf}:
                  {group: NavGroup; expanded: boolean; active: boolean; onClick: () => void;
                   items: NavItem[]; onChild: (item: NavItem) => void; onSelf: () => void}) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

    // COLLAPSED: an icon rail, with the section itself one click away.
    //
    // A 64px strip cannot render a label, let alone eight of them, so a group's children were simply
    // unreachable while it was shrunk -- clicking Catalog went to whichever child happened to be
    // first, which is a guess dressed up as navigation. The icon now opens the section: the group's
    // name, then its children, exactly the list the expanded rail shows beneath it.
    //
    // A MENU RATHER THAN A HOVER FLYOUT, and rather than an absolutely-positioned panel. The rail
    // scrolls (overflowY), so a panel positioned inside it is clipped by its own container; a menu
    // portals out. Click rather than hover because a hover panel over a scrolling 64px strip opens
    // when the pointer is only passing through on its way somewhere else.
    if (!expanded) {
        const hasChildren = items.length > 0;
        return (
            <>
                <Tooltip title={group.label} placement="right">
                    <div
                        role="button"
                        // NAMED, because collapsed there is no text: a 40x40 icon is the whole
                        // control, and a reader on a screen reader -- or a test -- has nothing else
                        // to go on. The tooltip is a hover affordance, not a name.
                        aria-label={group.label}
                        onClick={event => {
                            if (hasChildren) { setAnchor(event.currentTarget as HTMLElement); return; }
                            onClick();
                        }}
                        style={{display: "flex", justifyContent: "center", cursor: "pointer", padding: "4px 0"}}
                    >
                        <IconButton
                            className={css_buttons.blueIconButton}
                            style={{
                                height: "40px", width: "40px", flexShrink: 0,
                                backgroundColor: active ? ACTIVE_BG : undefined,
                            }}
                        >
                            {group.icon}
                        </IconButton>
                    </div>
                </Tooltip>

                <Menu
                    open={!!anchor}
                    anchorEl={anchor}
                    onClose={() => setAnchor(null)}
                    anchorOrigin={{vertical: "top", horizontal: "right"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    slotProps={{paper: {style: {minWidth: 208, marginLeft: 6}}}}
                >
                    {/* THE GROUP ITSELF, WHEN IT IS SOMEWHERE YOU CAN GO. Home both is a page and
                        has dashboards beneath it, and collapsed the icon now opens this menu -- so
                        without a row of its own, Home would be the one destination in the rail you
                        could not reach while the rail was shrunk.

                        A group that is only a name for what follows keeps the plain heading: it is
                        not a destination, and rendering it as one would make it look clickable. */}
                    {group.route ? (
                        <MenuItem
                            onClick={() => { setAnchor(null); onSelf(); }}
                            style={{fontSize: 13.5, fontWeight: 600, color: active ? Blue3 : Grey7,
                                    paddingTop: 6, paddingBottom: 6}}
                        >
                            {group.label}
                        </MenuItem>
                    ) : (
                        <Typography style={{
                            fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                            color: Grey6, padding: "8px 16px 4px",
                        }}>
                            {group.label}
                        </Typography>
                    )}

                    {items.map(child => (child.heading ? (
                        <Typography
                            key={"heading-" + child.label}
                            style={{
                                fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                                color: Grey6, padding: "10px 16px 4px",
                            }}
                        >
                            {child.label}
                        </Typography>
                    ) : (
                        <MenuItem
                            key={child.label + child.route}
                            onClick={() => { setAnchor(null); onChild(child); }}
                            style={{
                                fontSize: 13.5,
                                color: child.placeholder ? Grey6 : Grey7,
                                paddingTop: 6, paddingBottom: 6,
                            }}
                        >
                            {child.label}
                        </MenuItem>
                    )))}
                </Menu>
            </>
        );
    }

    // EXPANDED: a heading, or a destination that happens to have an icon. A group that is ONLY a
    // heading takes no active tint and no accent bar -- it is not somewhere you are, it is what the
    // things beneath it are called.
    const isDestination = !!group.route || !group.children;
    const highlighted = isDestination && active;

    return (
        <div
            onClick={onClick}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                cursor: "pointer",
                padding: "7px 16px",
                backgroundColor: highlighted ? ACTIVE_BG : undefined,
                borderLeft: highlighted ? `3px solid ${Blue3}` : "3px solid transparent",
            }}
        >
            <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 22, height: 22, flexShrink: 0,
                color: highlighted ? Blue3 : Grey7,
            }}>
                {group.icon}
            </span>
            <Typography style={{
                fontSize: 14,
                fontWeight: 600,
                color: highlighted ? Blue3 : Grey7,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
                {group.label}
            </Typography>
        </div>
    );
}

/**
 * A child: text alone, indented to sit under its group's LABEL rather than under its icon, which is
 * how the rail being followed aligns them. 48px = 16px padding + 22px icon + 12px gap, less the 3px
 * accent border the row carries itself.
 */
function ChildRow({item, active, onClick}: {item: NavItem; active: boolean; onClick: () => void}) {
    // A SECTION NAME, at the children's own indent so the names below it line up under it rather
    // than under the heading's first letter. Smaller and quieter than the rail's own "Workspace"
    // heading, because it sits INSIDE a group rather than dividing the rail.
    if (item.heading) {
        return (
            <Typography style={{
                fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                color: Grey6, padding: "10px 16px 2px 51px",
            }}>
                {item.label}
            </Typography>
        );
    }

    return (
        <div
            onClick={onClick}
            style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "5px 16px 5px 48px",
                borderLeft: active ? `3px solid ${Blue3}` : "3px solid transparent",
                backgroundColor: active ? ACTIVE_BG : undefined,
            }}
        >
            <Typography style={{
                fontSize: 13.5,
                fontWeight: active ? 600 : 400,
                // A destination with nothing behind it yet reads quieter than one that works, so the
                // rail shows the whole shape without overstating what is there.
                color: active ? Blue3 : (item.placeholder ? Grey6 : Grey7),
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
                {item.label}
            </Typography>
        </div>
    );
}
